import { forwardRef, useContext, useImperativeHandle, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Flex, InputField, Separator, toast } from '@raystack/apsara';
import { CreateProjectRequestSchema } from '@raystack/frontier';
import {
  FrontierServiceQueries,
  create,
  useMutation
} from '@raystack/frontier/hooks';
import { Modal } from '@studio/components';
import * as yup from 'yup';

import { IdentityContext } from '@src/contexts/identity/context';
import { useLastSelectedProject } from '@src/hooks/useLastSelectedProject';
import { useProjects } from '@src/hooks/useProjects';

import styles from './style.module.css';

type ConnectError = {
  code?: number;
  message?: string;
};

function generateHash(org_id: string): string {
  if (!org_id) return '';

  // Step 2: Create a deterministic short hash (based on string content)
  // Uses a simple non-cryptographic hash algorithm (FNV-1a-like)
  let hash = 0;
  for (let i = 0; i < org_id.length; i++) {
    hash = (hash * 31 + org_id.charCodeAt(i)) >>> 0; // unsigned 32-bit
  }

  // Convert to base36 for compact, GitHub-style output
  const shortHash = hash.toString(36).slice(-6); // 6 chars max

  // const combined = `${title}-${shortHash}`;

  // return combined;
  return shortHash;
}

function generateNameFromTitle(raw: string, org_id: string) {
  // Lowercase, trim, spaces->dash, strip invalids, collapse dashes/underscores, trim edges
  const slug = (raw || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // spaces -> dash
    .replace(/[^a-z0-9_-]/g, '') // keep only [a-z0-9_-]
    .replace(/[-_]{2,}/g, '-') // collapse repeats
    .replace(/^[-_]+|[-_]+$/g, ''); // trim dashes/underscores

  return `${slug}-${generateHash(org_id)}`;
}

const projectSchema = yup
  .object({
    title: yup
      .string()
      .required('Please provide a project title')
      .max(100, 'Title must be less than 100 characters'),

    org_id: yup.string().required()
  })
  .required();
type FormData = yup.InferType<typeof projectSchema>;

interface CreateNewProjectPopupProps {
  setShowDialog: (value: boolean) => void;
}

interface CreateNewProjectPopupRef {
  setOpen: (open: boolean) => void;
}

export const CreateNewProjectPopup = forwardRef<
  CreateNewProjectPopupRef,
  CreateNewProjectPopupProps
>(function CreateNewProjectPopup({ setShowDialog }, ref) {
  const { activeOrg } = useContext(IdentityContext);

  const [open, setOpen] = useState(false);

  useImperativeHandle(ref, () => ({ setOpen }), []);

  const {
    reset,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(projectSchema),
    defaultValues: {
      org_id: activeOrg?.id
    }
  });

  const { mutateAsync: createProjectMutation } = useMutation(
    FrontierServiceQueries.createProject
  );
  const { getProjects } = useProjects({ canUserUpdate: true });
  const { setLastSelectedProjectId } = useLastSelectedProject();

  function onClose() {
    setOpen(false);
    setShowDialog(true);
  }

  async function onSubmit(data: FormData) {
    if (!activeOrg?.id) return;

    const derivedName = generateNameFromTitle(data.title, activeOrg.id);

    try {
      const response = await createProjectMutation(
        create(CreateProjectRequestSchema, {
          body: {
            orgId: activeOrg.id,
            title: data.title,
            name: derivedName
          }
        })
      );
      toast.success('Project Created');

      await getProjects();

      const newProj = response.project;
      if (newProj?.id) {
        setLastSelectedProjectId(newProj.id);
        onClose();
        reset();
      }
    } catch (err: unknown) {
      const error = err as ConnectError;
      if (error.code === 6) {
        // Code 6 = AlreadyExists (409 conflict)
        setError('title', {
          message:
            'A project with a similar generated identifier already exists. Please tweak the title and try again.'
        });
      } else {
        toast.error('Something went wrong', {
          description: error.message || 'An error occurred'
        });
      }
    }
  }

  return (
    <Modal
      show={open}
      title='Create project'
      onClose={onClose}
      footer={<></>}
      classes={{ body: styles.dialogContent }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction='column' className={styles.dialogBody}>
          <Controller
            render={({ field }) => (
              <InputField
                id='title'
                placeholder='Provide project title'
                label='Project title'
                onChange={field.onChange}
                onBlur={field.onBlur}
                helperText={errors.title && String(errors.title?.message)}
                data-test-id='title-input'
              />
            )}
            control={control}
            name='title'
          />
        </Flex>
        <Separator />
        <Flex className={styles.footer} gap='small' justify='end'>
          <Button
            variant='outline'
            color='neutral'
            onClick={onClose}
            data-test-id='modal-cancel-btn'
          >
            Cancel
          </Button>
          <Button
            type='submit'
            loading={isSubmitting}
            loaderText='Submitting'
            data-test-id='modal-submit-btn'
          >
            Submit
          </Button>
        </Flex>
      </form>
    </Modal>
  );
});
